import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Select,
  SelectItem,
  Card,
  Checkbox,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import {
  CtyButton,
  CtyShowToast,
  checkType,
  useHasApplicationResource,
} from '@circuitry-ai/doc-ui';
import {
  useGetCredentialsListQuery,
  useGetAiWorkersByCategoryQuery,
  useReprocessClaimsMutation,
  useTenantDetails,
  categoryType,
  type Credential,
  type ReprocessClaimsPayload,
  type ReprocessClaimFilter,
  type ReprocessPreviewResultData,
  type ReprocessTriggerResultData,
  type ReprocessUsageStatus,
} from '@circuitry-ai/doc-data';
import type { AIWorker } from 'modules/shared/data/src/features/aides/aiworkerTypes';

type ReprocessMode = 'preview' | 'trigger';
type DateFilterType = 'last_n_days' | 'date_range';

const CONFIRM_PHRASE = 'REPROCESS';

interface ReprocessFormState {
  usage_status: ReprocessUsageStatus;
  date_filter_type: DateFilterType;
  days: string;
  from_date: string;
  to_date: string;
  from_time: string;
  to_time: string;
  apply_limit: boolean;
  limit: number;
  aiworker_id: string;
  credential_id: string;
  reason: string;
}

type FieldErrors = Partial<Record<keyof ReprocessFormState, string>>;

const defaultFormState: ReprocessFormState = {
  usage_status: 'failed',
  date_filter_type: 'last_n_days',
  days: '',
  from_date: '',
  to_date: '',
  from_time: '',
  to_time: '',
  apply_limit: false,
  limit: 500,
  aiworker_id: '',
  credential_id: '',
  reason: '',
};

const usageStatusOptionKeys: { key: ReprocessUsageStatus; labelKey: string }[] = [
  { key: 'failed', labelKey: 'Failed runs only' },
  { key: 'succeeded', labelKey: 'Successful runs only' },
  { key: 'in_progress', labelKey: 'In progress runs only' },
  { key: 'all', labelKey: 'All statuses' },
];

function buildClaimFilter(form: ReprocessFormState): ReprocessClaimFilter {
  const filter: ReprocessClaimFilter = {
    usage_status: form.usage_status,
    date_mode: form.date_filter_type === 'last_n_days' ? 'relative' : 'range',
  };

  if (form.date_filter_type === 'last_n_days') {
    filter.days = Number(form.days);
  } else {
    filter.from = form.from_time ? `${form.from_date}T${form.from_time}:00Z` : form.from_date;
    filter.to = form.to_time ? `${form.to_date}T${form.to_time}:00Z` : form.to_date;
  }

  if (form.apply_limit && form.limit > 0) {
    filter.limit = Number(form.limit);
  }
  return filter;
}

function buildPayload(form: ReprocessFormState, mode: ReprocessMode): ReprocessClaimsPayload {
  const payload: ReprocessClaimsPayload = {
    source: 'ui',
    trigger_agent: mode === 'trigger',
    reason: form.reason.trim(),
    aiworker_id: form.aiworker_id,
    claim_filter: buildClaimFilter(form),
  };
  if (mode === 'trigger') payload.credential_id = form.credential_id;
  return payload;
}

interface StatTileProps {
  label: string;
  value: string | number;
  emphasize?: boolean;
}

function StatTile({ label, value, emphasize }: Readonly<StatTileProps>) {
  return (
    <div className="border rounded-md p-4 bg-content1 min-w-[140px] flex-1">
      <div className={`font-bold ${emphasize ? 'text-2xl' : 'text-lg'} truncate`} title={String(value)}>
        {value}
      </div>
      <div className="text-sm text-default-500 mt-1">{label}</div>
    </div>
  );
}

interface ClaimNumbersTableProps {
  claimNumbers: string[];
}

function ClaimNumbersTable({ claimNumbers }: Readonly<ClaimNumbersTableProps>) {
  const { t } = useTranslation();
  const rows = useMemo(
    () => claimNumbers.map((claim_number, idx) => ({ index: idx + 1, claim_number })),
    [claimNumbers]
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { headerName: '#', field: 'index', width: 80, sortable: false, filter: false },
      { headerName: t('Claim Number'), field: 'claim_number', flex: 1, sortable: true, filter: false },
    ],
    [t]
  );

  return (
    <div className="ag-theme-alpine w-full mt-4" style={{ '--ag-border-color': '#e0e0e0' } as React.CSSProperties}>
      <div className="text-sm text-default-500 mb-2 font-medium">{t('Claim Numbers')}</div>
      <AgGridReact
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={{ resizable: true }}
        domLayout="autoHeight"
        headerHeight={40}
        rowHeight={40}
        pagination
        paginationPageSize={10}
        paginationPageSizeSelector={[10, 20, 50]}
      />
    </div>
  );
}

interface ConfirmTriggerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isConfirming: boolean;
  tenantName: string;
  aiWorkerLabel: string;
  credentialLabel: string;
  reason: string;
}

function ConfirmTriggerModal({
  isOpen,
  onClose,
  onConfirm,
  isConfirming,
  tenantName,
  aiWorkerLabel,
  credentialLabel,
  reason,
}: Readonly<ConfirmTriggerModalProps>) {
  const { t } = useTranslation();
  const [phrase, setPhrase] = useState('');
  const isMatch = phrase.trim().toUpperCase() === CONFIRM_PHRASE;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setPhrase('');
          onClose();
        }
      }}
      placement="center"
      size="md"
      hideCloseButton
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span>{t('Confirm trigger')}</span>
          <span className="text-sm font-normal text-default-500">
            {t('This will enqueue real workflow messages. The action is audited.')}
          </span>
        </ModalHeader>
        <ModalBody className="gap-2">
          <div className="flex justify-between text-sm">
            <span className="text-default-500">{t('Tenant')}</span>
            <span className="font-medium">{tenantName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-default-500">{t('AI Worker')}</span>
            <span className="font-medium">{aiWorkerLabel}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-default-500">{t('Target agent')}</span>
            <span className="font-medium">{credentialLabel}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-default-500">{t('Reason')}</span>
            <span className="font-medium break-all text-right ml-4">{reason}</span>
          </div>
          <div className="mt-3">
            <div className="text-sm text-default-500 mb-1 font-medium">
              {t('Type "{{phrase}}" to confirm', { phrase: 'reprocess' })}
            </div>
            <Input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              size="sm"
              placeholder={t('reprocess')}
              autoFocus
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <CtyButton variant="light" onPress={onClose} isDisabled={isConfirming}>
            {t('Cancel')}
          </CtyButton>
          <CtyButton color="danger" onPress={onConfirm} isDisabled={!isMatch || isConfirming}>
            {t('Trigger')}
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (val: T) => void;
  options: { key: T; label: string }[];
}

function SegmentedControl<T extends string>({ value, onChange, options }: Readonly<SegmentedControlProps<T>>) {
  return (
    <div className="inline-flex border rounded-md overflow-hidden bg-default-100">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`px-4 py-1.5 text-sm font-medium transition-colors ${
            value === opt.key ? 'bg-primary text-white' : 'bg-transparent text-default-600 hover:bg-default-100'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

interface FieldLabelProps {
  text: string;
  required?: boolean;
  hint?: string;
}

function FieldLabel({ text, required, hint }: Readonly<FieldLabelProps>) {
  return (
    <span className="text-sm font-medium text-default-600">
      {text} {required && <span className="text-danger">*</span>}
      {hint && <span className="text-default-400 text-xs ml-1">{hint}</span>}
    </span>
  );
}

export function ReprocessSettings() {
  const { t } = useTranslation();
  const hasSchedulerAccess = useHasApplicationResource('scheduler');
  const [mode, setMode] = useState<ReprocessMode>('preview');
  const [form, setForm] = useState<ReprocessFormState>(defaultFormState);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [previewResult, setPreviewResult] = useState<ReprocessPreviewResultData | null>(null);
  const [triggerResult, setTriggerResult] = useState<ReprocessTriggerResultData | null>(null);
  const [resultKind, setResultKind] = useState<ReprocessMode | null>(null);
  const [resultSnapshot, setResultSnapshot] = useState<ReprocessFormState | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: credentialsList, isLoading: isLoadingCredentials } = useGetCredentialsListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: aiWorkers, isLoading: isLoadingAiWorkers } = useGetAiWorkersByCategoryQuery({
    category: categoryType.AGENT,
  });
  const [reprocessClaims, { isLoading: isSubmitting }] = useReprocessClaimsMutation();
  const { tenantDetails } = useTenantDetails();

  const tenantName = tenantDetails?.org_name || t('Current tenant');
  const credentials: Credential[] = useMemo(() => credentialsList || [], [credentialsList]);
  const agents: AIWorker[] = useMemo(() => {
    if (!aiWorkers) return [];
    if (Array.isArray(aiWorkers)) return aiWorkers;
    return (aiWorkers as { agents?: AIWorker[] }).agents ?? [];
  }, [aiWorkers]);

  const selectedAiWorker = useMemo(
    () => agents.find((a) => a.aiworker_id === form.aiworker_id),
    [agents, form.aiworker_id]
  );
  const selectedCredential = useMemo(
    () => credentials.find((c) => c.credential_id === form.credential_id),
    [credentials, form.credential_id]
  );

  const updateField = useCallback(<K extends keyof ReprocessFormState>(key: K, value: ReprocessFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const buildValidationErrors = useCallback(
    (m: ReprocessMode): FieldErrors => {
      const next: FieldErrors = {};
      if (!form.aiworker_id) next.aiworker_id = t('AI Worker is required');
      if (!form.reason.trim()) next.reason = t('Reason / Ticket is required');
      if (form.date_filter_type === 'last_n_days') {
        const numDays = Number(form.days);
        if (!form.days || Number.isNaN(numDays) || numDays <= 0) {
          next.days = t('Days must be greater than 0');
        }
      } else {
        if (!form.from_date) next.from_date = t('From date is required');
        if (!form.to_date) next.to_date = t('To date is required');
      }
      if (form.apply_limit && (!form.limit || form.limit <= 0)) {
        next.limit = t('Limit must be greater than 0');
      }
      if (m === 'trigger' && !form.credential_id) {
        next.credential_id = t('Target Agent (Credential) is required');
      }
      return next;
    },
    [form, t]
  );

  const submit = useCallback(
    async (m: ReprocessMode) => {
      const validationErrors = buildValidationErrors(m);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
      setErrors({});
      if (m === 'trigger') setConfirmOpen(false);
      try {
        const payload = buildPayload(form, m);
        const response = await reprocessClaims(payload).unwrap();
        const data = response?.data;
        setResultKind(m);
        setResultSnapshot({ ...form });
        if (m === 'preview') {
          setPreviewResult(data as ReprocessPreviewResultData);
          setTriggerResult(null);
          CtyShowToast(checkType.success, t('Reprocess preview completed'));
        } else {
          setTriggerResult(data as ReprocessTriggerResultData);
          setPreviewResult(null);
          CtyShowToast(checkType.success, t('Reprocess triggered'));
        }
      } catch (err) {
        CtyShowToast(
          checkType.error,
          `${t('Reprocess request failed')}: ${(err as Error)?.message || t('Something went wrong')}`
        );
      }
    },
    [form, reprocessClaims, t, buildValidationErrors]
  );

  const handleConfirmTrigger = useCallback(() => {
    submit('trigger');
  }, [submit]);

  const handlePrimaryClick = useCallback(() => {
    if (mode === 'preview') {
      submit('preview');
    } else {
      const validationErrors = buildValidationErrors('trigger');
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
      setErrors({});
      setConfirmOpen(true);
    }
  }, [mode, submit, buildValidationErrors]);

  const adjustDays = useCallback(
    (delta: number) => {
      const current = Number(form.days) || 0;
      const next = Math.max(1, current + delta);
      updateField('days', String(next));
    },
    [form.days, updateField]
  );

  const usageStatusLabel = useMemo(() => {
    if (!resultSnapshot) return '';
    const opt = usageStatusOptionKeys.find((o) => o.key === resultSnapshot.usage_status);
    return opt ? t(opt.labelKey) : '';
  }, [resultSnapshot, t]);

  const dateWindowLabel = useMemo(() => {
    if (!resultSnapshot) return '';
    return resultSnapshot.date_filter_type === 'date_range' ? t('range') : `${resultSnapshot.days}d`;
  }, [resultSnapshot, t]);

  const limitLabel = useMemo(() => {
    if (!resultSnapshot) return '';
    return resultSnapshot.apply_limit ? String(resultSnapshot.limit) : t('none');
  }, [resultSnapshot, t]);

  const handleUsageStatusChange = (keys: unknown) => {
    const value = Array.from(keys as Set<string>)[0] as ReprocessUsageStatus | undefined;
    if (value) updateField('usage_status', value);
  };

  const handleAiWorkerChange = (keys: unknown) => {
    const value = Array.from(keys as Set<string>)[0];
    updateField('aiworker_id', value || '');
  };

  const handleCredentialChange = (keys: unknown) => {
    const value = Array.from(keys as Set<string>)[0];
    updateField('credential_id', value || '');
  };

  const daysEndContent = (
    <div className="flex flex-col -mr-2">
      <button
        type="button"
        aria-label={t('Increase days')}
        className="h-3.5 w-6 flex items-center justify-center hover:bg-default-100 rounded-t"
        onClick={() => adjustDays(1)}
      >
        <Icon icon="mdi:chevron-up" className="text-default-500" width="14" height="14" />
      </button>
      <button
        type="button"
        aria-label={t('Decrease days')}
        className="h-3.5 w-6 flex items-center justify-center hover:bg-default-100 rounded-b"
        onClick={() => adjustDays(-1)}
      >
        <Icon icon="mdi:chevron-down" className="text-default-500" width="14" height="14" />
      </button>
    </div>
  );

  const primaryButton = (
    <div className="flex items-center gap-3">
      <CtyButton
        color={mode === 'trigger' ? 'danger' : 'primary'}
        onPress={handlePrimaryClick}
        isLoading={isSubmitting}
        isDisabled={!hasSchedulerAccess || isSubmitting}
      >
        {mode === 'preview' ? t('Run Preview') : t('Trigger Reprocess')}
      </CtyButton>
      {mode === 'trigger' && (
        <span className="text-sm text-default-500">{t('Run preview before trigger.')}</span>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4 pb-6">
      <Card className="p-6 space-y-5 mb-2">
        <div>
          <h3 className="font-semibold text-base">{t('New reprocess run')}</h3>
          <p className="text-sm text-default-500 mt-1">
            {t('Pick usage status and a date window')}
          </p>
        </div>

        <div className="space-y-1.5">
          <FieldLabel text={t('Mode')} required />
          <div>
            <SegmentedControl<ReprocessMode>
              value={mode}
              onChange={setMode}
              options={[
                { key: 'preview', label: t('Preview') },
                { key: 'trigger', label: t('Trigger') },
              ]}
            />
          </div>
          <p className="text-sm text-default-500">
            {t('Preview lists claim numbers. Trigger enqueues after a successful preview.')}
          </p>
        </div>

        <div className="space-y-1.5">
          <FieldLabel text={t('Usage Status')} required />
          <Select
            aria-label={t('Usage Status')}
            selectedKeys={[form.usage_status]}
            onSelectionChange={handleUsageStatusChange}
            size="sm"
          >
            {usageStatusOptionKeys.map((opt) => (
              <SelectItem key={opt.key} value={opt.key}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </Select>
        </div>

        <div className="space-y-2">
          <FieldLabel text={t('Date Filter')} required />
          <div>
            <SegmentedControl<DateFilterType>
              value={form.date_filter_type}
              onChange={(v) => updateField('date_filter_type', v)}
              options={[
                { key: 'last_n_days', label: t('Last N days') },
                { key: 'date_range', label: t('Date range') },
              ]}
            />
          </div>

          {form.date_filter_type === 'last_n_days' ? (
            <div className="space-y-1.5">
              <FieldLabel text={t('Days')} required />
              <Input
                type="number"
                min={1}
                value={form.days}
                onChange={(e) => updateField('days', e.target.value)}
                size="sm"
                placeholder={t('e.g. 7')}
                isInvalid={!!errors.days}
                errorMessage={errors.days}
                endContent={daysEndContent}
                classNames={{
                  input: '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                }}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <FieldLabel text={t('From Date (UTC)')} required />
                <Input
                  type="date"
                  size="sm"
                  value={form.from_date}
                  onChange={(e) => updateField('from_date', e.target.value)}
                  isInvalid={!!errors.from_date}
                  errorMessage={errors.from_date}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel text={t('To Date (UTC)')} required />
                <Input
                  type="date"
                  size="sm"
                  value={form.to_date}
                  onChange={(e) => updateField('to_date', e.target.value)}
                  isInvalid={!!errors.to_date}
                  errorMessage={errors.to_date}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel text={t('From Time (UTC, optional)')} />
                <Input
                  type="time"
                  size="sm"
                  value={form.from_time}
                  onChange={(e) => updateField('from_time', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel text={t('To Time (UTC, optional)')} />
                <Input
                  type="time"
                  size="sm"
                  value={form.to_time}
                  onChange={(e) => updateField('to_time', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <FieldLabel text={t('Row Limit (Optional)')} />
          <div className="flex items-center gap-3">
            <Checkbox
              isSelected={form.apply_limit}
              onValueChange={(checked) => updateField('apply_limit', checked)}
            >
              <span className="text-sm">{t('Apply LIMIT')}</span>
            </Checkbox>
            <div className="flex-1">
              <Input
                type="number"
                min={1}
                value={String(form.limit)}
                onChange={(e) => updateField('limit', Number(e.target.value) || 0)}
                isDisabled={!form.apply_limit}
                size="sm"
                isInvalid={!!errors.limit}
                errorMessage={errors.limit}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <FieldLabel text={t('AI Worker')} required />
            <Select
              aria-label={t('AI Worker')}
              placeholder={isLoadingAiWorkers ? t('Loading...') : t('Select AI Worker')}
              selectedKeys={form.aiworker_id ? [form.aiworker_id] : []}
              onSelectionChange={handleAiWorkerChange}
              size="sm"
              isDisabled={isLoadingAiWorkers}
              isInvalid={!!errors.aiworker_id}
              errorMessage={errors.aiworker_id}
            >
              {agents.map((a) => (
                <SelectItem key={a.aiworker_id} value={a.aiworker_id}>
                  {a.name}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <FieldLabel
              text={t('Target Agent (Credential)')}
              required={mode === 'trigger'}
              hint={t('(Trigger only)')}
            />
            <Select
              aria-label={t('Target Agent')}
              placeholder={isLoadingCredentials ? t('Loading...') : t('Select credential')}
              selectedKeys={form.credential_id ? [form.credential_id] : []}
              onSelectionChange={handleCredentialChange}
              size="sm"
              isDisabled={isLoadingCredentials}
              isInvalid={!!errors.credential_id}
              errorMessage={errors.credential_id}
            >
              {credentials.map((c) => (
                <SelectItem key={c.credential_id} value={c.credential_id}>
                  {c.name}
                </SelectItem>
              ))}
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <FieldLabel text={t('Reason / Ticket')} required />
          <Input
            placeholder={t('e.g. JIRA-1284 — re-run failed claims from last week')}
            value={form.reason}
            onChange={(e) => updateField('reason', e.target.value)}
            size="sm"
            isInvalid={!!errors.reason}
            errorMessage={errors.reason}
          />
        </div>

        <div className="pt-2">{primaryButton}</div>
      </Card>

      {resultKind === 'preview' && previewResult && resultSnapshot && (
        <Card className="p-6 space-y-4 mb-2">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-base">{t('Result')}</h3>
            <span className="text-sm px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">
              {t('Preview')}
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            <StatTile label={t('Distinct Claims')} value={previewResult.distinct_claim_count} emphasize />
            <StatTile label={t('Usage Status')} value={usageStatusLabel} />
            <StatTile label={t('Date Window')} value={dateWindowLabel} />
            <StatTile label={t('Limit')} value={limitLabel} />
          </div>
          <ClaimNumbersTable claimNumbers={previewResult.claim_numbers || []} />
        </Card>
      )}

      {resultKind === 'trigger' && triggerResult && (
        <Card className="p-6 space-y-4 mb-2">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-base">{t('Result')}</h3>
            <span className="text-sm px-2 py-0.5 rounded bg-danger/10 text-danger font-medium">
              {t('Trigger')}
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            <StatTile label={t('Total Items')} value={triggerResult.total_items} emphasize />
            <StatTile label={t('Successful')} value={triggerResult.successful_count} />
            <StatTile label={t('Failed')} value={triggerResult.failed_count} />
            <StatTile label={t('Skipped')} value={triggerResult.skipped_count} />
          </div>
          <ClaimNumbersTable claimNumbers={triggerResult.claim_numbers || []} />
        </Card>
      )}

      <ConfirmTriggerModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmTrigger}
        isConfirming={isSubmitting}
        tenantName={tenantName}
        aiWorkerLabel={selectedAiWorker ? selectedAiWorker.name : ''}
        credentialLabel={selectedCredential ? selectedCredential.name : ''}
        reason={form.reason}
      />
    </div>
  );
}

export default ReprocessSettings;
